import { useTranslation } from "react-i18next";
import { blogPosts, getPostBySlug, latestPosts } from "./posts";
import type { BlogPost } from "./types";
import { formatBlogDate } from "./date";

const APP_URL = import.meta.env.VITE_APP_URL ?? "https://app.andrum.app";

export function BlogIndex({
  onOpenPost,
  onHome,
}: {
  onOpenPost: (slug: string) => void;
  onHome: () => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <section className="section blog-section">
      <div className="section-head">
        <p className="eyebrow">{t("blog.eyebrow")}</p>
        <h1>{t("blog.title")}</h1>
        <p className="section-sub">{t("blog.lead")}</p>
      </div>
      <div className="blog-list">
        {blogPosts.map((post) => (
          <article key={post.slug} className="card blog-card">
            <p className="blog-date">
              <time dateTime={post.date}>
                {formatBlogDate(post.date, i18n.resolvedLanguage ?? i18n.language)}
              </time>
            </p>
            <h2>
              <a
                href={`/blog/${post.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  onOpenPost(post.slug);
                }}
              >
                {post.title}
              </a>
            </h2>
            <p>{post.description}</p>
            <a
              className="btn btn-ghost"
              href={`/blog/${post.slug}`}
              onClick={(e) => {
                e.preventDefault();
                onOpenPost(post.slug);
              }}
            >
              {t("blog.read")}
            </a>
          </article>
        ))}
      </div>
      <p className="blog-back">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            onHome();
          }}
        >
          ← {t("blog.backHome")}
        </a>
      </p>
    </section>
  );
}

export function BlogPostPage({
  slug,
  onBack,
  onHome,
}: {
  slug: string;
  onBack: () => void;
  onHome: () => void;
}) {
  const { t, i18n } = useTranslation();
  const post = getPostBySlug(slug);
  if (!post) {
    return (
      <section className="section">
        <h1>{t("blog.notFound")}</h1>
        <a
          href="/blog"
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          {t("blog.backList")}
        </a>
      </section>
    );
  }

  return (
    <article className="section blog-post">
      <p className="blog-date">
        <time dateTime={post.date}>
          {formatBlogDate(post.date, i18n.resolvedLanguage ?? i18n.language)}
        </time>
      </p>
      <h1>{post.title}</h1>
      <p className="lead">{post.description}</p>
      <div
        className="blog-body prose"
        dangerouslySetInnerHTML={{ __html: post.html }}
      />
      <div className="blog-cta card">
        <h2>{t("blog.ctaTitle")}</h2>
        <p>{t("blog.ctaBody")}</p>
        <a className="btn btn-primary" href={APP_URL}>
          {t("blog.ctaButton")}
        </a>
      </div>
      <p className="blog-back">
        <a
          href="/blog"
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          ← {t("blog.backList")}
        </a>
        {" · "}
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            onHome();
          }}
        >
          {t("blog.backHome")}
        </a>
      </p>
    </article>
  );
}

export function BlogTeaser({
  onOpenBlog,
  onOpenPost,
}: {
  onOpenBlog: () => void;
  onOpenPost: (slug: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const posts = latestPosts(3);
  if (posts.length === 0) return null;
  return (
    <section className="section" id="blog">
      <div className="section-head">
        <h2>{t("blog.teaserTitle")}</h2>
        <p className="section-sub">{t("blog.teaserLead")}</p>
      </div>
      <div className="grid-3">
        {posts.map((post: BlogPost) => (
          <article key={post.slug} className="card">
            <p className="blog-date">
              <time dateTime={post.date}>
                {formatBlogDate(post.date, i18n.resolvedLanguage ?? i18n.language)}
              </time>
            </p>
            <h3>
              <a
                href={`/blog/${post.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  onOpenPost(post.slug);
                }}
              >
                {post.title}
              </a>
            </h3>
            <p>{post.description}</p>
          </article>
        ))}
      </div>
      <div className="hero-actions" style={{ marginTop: "1.25rem" }}>
        <a
          className="btn btn-ghost"
          href="/blog"
          onClick={(e) => {
            e.preventDefault();
            onOpenBlog();
          }}
        >
          {t("blog.allPosts")}
        </a>
      </div>
    </section>
  );
}

export function applyBlogMeta(post: BlogPost | "index" | null) {
  const setMeta = (selector: string, attr: string, value: string) => {
    const el = document.querySelector(selector);
    if (el) el.setAttribute(attr, value);
  };
  if (post === "index") {
    document.title = "Blogg | andrum: insikter om välmående och mikroexperiment";
    setMeta(
      'meta[name="description"]',
      "content",
      "Artiklar om mikrovanor, dagens insikt, AI-coachning och forskning i vardagsdos från andrum."
    );
    setMeta(
      'meta[property="og:title"]',
      "content",
      "Blogg | andrum"
    );
    setMeta(
      'meta[property="og:description"]',
      "content",
      "Mjuka artiklar om välmående, experiment och AI som lär av dig."
    );
    setMeta('meta[property="og:url"]', "content", "https://andrum.app/blog");
    return;
  }
  if (!post) return;
  document.title = `${post.title} | andrum`;
  setMeta('meta[name="description"]', "content", post.description);
  setMeta('meta[property="og:title"]', "content", post.title);
  setMeta('meta[property="og:description"]', "content", post.description);
  setMeta(
    'meta[property="og:url"]',
    "content",
    post.canonical ?? `https://andrum.app/blog/${post.slug}`
  );
  const existing = document.getElementById("andrum-jsonld-blog");
  if (existing) existing.remove();
  const graph = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    author: { "@type": "Organization", name: "andrum" },
    publisher: { "@type": "Organization", name: "andrum", url: "https://andrum.app" },
    mainEntityOfPage: post.canonical ?? `https://andrum.app/blog/${post.slug}`,
    keywords: post.keywords.join(", "),
  };
  const script = document.createElement("script");
  script.id = "andrum-jsonld-blog";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(graph);
  document.head.appendChild(script);
}
